import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { Card, mergeClasses, Title2, Caption2, Caption1Strong, Body2, ToggleButton } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { AdsStudioSpinner, SectionTilesGrid } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useTemplateVideoStyles, useTemplateGridStyles } from './style';

export const VideoTemplateCard = ({
  template,
  i18n,
  getAssetCountInfo,
}) => {
  const classes = useTemplateVideoStyles();

  return (
    <div className={mergeClasses(classes.flexColumn, classes.templateWithCaption)}>
      <Card className={classes.videoCard} aria-label={template.LocalizedTemplateName || template.TemplateName}>
        <img className={classes.videoCardThumbnail} src={template.TemplateThumbnailUrl} alt={i18n.getString(_TL_('Preview of video ad template'))} />
      </Card>
      <div className={mergeClasses(classes.flexColumn, classes.caption)}>
        <Caption1Strong className={classes.darkGrayText}> {template.LocalizedTemplateName || template.TemplateName} </Caption1Strong>
        <Caption2 className={classes.grayText}> {getAssetCountInfo(template)} </Caption2>
      </div>
    </div>
  );
};

VideoTemplateCard.propTypes = {
  template: PropTypes.shape({
    TemplateId: PropTypes.string.isRequired,
    TemplateName: PropTypes.string.isRequired,
    Duration: PropTypes.number,
    AspectRatio: PropTypes.string,
    Themes: PropTypes.arrayOf(PropTypes.string),
    NumberOfImages: PropTypes.number,
    NumberOfText: PropTypes.number,
    TemplateThumbnailUrl: PropTypes.string.isRequired,
    TemplatePreviewUrl: PropTypes.string.isRequired,
    // NumberOfLogos: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  getAssetCountInfo: PropTypes.func.isRequired,
};

export const TemplateGrid = ({
  templates,
  setClickedTemplate,
  loading,
  i18n,
  getAssetCountInfo,
  templateTagOptions,
  lastTriggerRef,
}) => {
  const classes = useTemplateGridStyles();


  const [selectedFilter, setSelectedFilter] = useState(templateTagOptions.all);
  const filterOptions = Object.values(templateTagOptions);

  const getTitleMessage = useMemo(() => (
    (templates && templates.length) ? i18n.getString(_TL_('Video templates')) : i18n.getString(_TL_('No templates found'))
  ), [templates, i18n]);

  const onItemAction = useCallback((event, template, source) => {
    const target = (event && event.currentTarget) || (event.detail && event.detail.originalEvent && event.detail.originalEvent.currentTarget);

    if (source === 'action' || (source === 'keydown' && event && event.key === 'Enter')) {
      event.preventDefault();
      if (lastTriggerRef && target) {
        // eslint-disable-next-line no-param-reassign
        lastTriggerRef.current = target;
      }

      setClickedTemplate(template);
    }
  }, [setClickedTemplate, lastTriggerRef]);

  const filteredTemplates = useMemo(
    () =>
      templates.filter(template => template && (selectedFilter === templateTagOptions.all || (template && template.tags && template.tags.includes(selectedFilter))))
        .map(template =>
          ({
            ...template,
            TemplateThumbnailUrl: template.TemplateThumbnailUrl,
            onAction: event => onItemAction(event, template, 'action'),
            handleKeyDown: event => onItemAction(event, template, 'keydown'),
          }))
    , [templates, selectedFilter, templateTagOptions, onItemAction]
  );

  const onRenderCellContent = useCallback((item) => {
    const templateItem = item.item || item;
    if (!templateItem) {
      return null;
    }
    const { TemplateId } = templateItem;
    return (
      <VideoTemplateCard
        key={TemplateId}
        template={templateItem}
        i18n={i18n}
        getAssetCountInfo={getAssetCountInfo}
      />
    );
  }, [i18n, getAssetCountInfo]);

  const templatesGridSection = useMemo(() => ({
    sectionName: i18n.getString(_TL_('Video templates')),
    sectionClassName: 'video-templates-section',
    itemKey: 'id',
    items: filteredTemplates,
    totalItemCount: filteredTemplates && filteredTemplates.length,
    config: {
      scrollContainer: '[role="main"]',
      minItemHeight: 42,
    },
  }), [filteredTemplates, i18n]);

  const sectionGridData = useMemo(
    () => ([templatesGridSection]),
    [templatesGridSection]
  );

  return (
    <>
      {loading ? (
        <div aria-busy="true" aria-label={i18n.getString(_TL_('Loading templates'))}>
          <Overlay>
            <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 250 }} />
          </Overlay>
        </div>
      ) : (
        <div>
          <Title2 block>{ getTitleMessage }</Title2>
          <h1 className={classes.screenReaderOnly}>{getTitleMessage}</h1>
          {
            (templates && templates.length > 0) &&
            <div>
              <div className={classes.templateBody}>
                <Body2 className="video-templates-banner-description">
                  {i18n.getString(_TL_('Create a video using a template.'))}
                </Body2>
              </div>
              <div className={mergeClasses('template-filter', classes.filtersRow)}>
                {filterOptions.map((filter, index) => (
                  <React.Fragment key={filter}>
                    <ToggleButton
                      onClick={() => setSelectedFilter(filter)}
                      className={mergeClasses(`filter-${index}`, classes.filterButton)}
                      disabled={loading}
                      checked={selectedFilter === filter}
                    >
                      {i18n.getString(filter)}
                    </ToggleButton>
                  </React.Fragment>
                ))}
              </div>
              <div className={mergeClasses('template-grid', classes.gridContainer)}>
                <SectionTilesGrid
                  sectionData={sectionGridData}
                  onRenderCell={onRenderCellContent}
                  loadNextPage={() => {}}
                />
              </div>
            </div>
          }
        </div>
      )}
    </>
  );
};

TemplateGrid.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    TemplateId: PropTypes.string.isRequired,
    TemplateName: PropTypes.string.isRequired,
    Duration: PropTypes.number,
    AspectRatio: PropTypes.string,
    Themes: PropTypes.arrayOf(PropTypes.string),
    NumberOfImages: PropTypes.number,
    NumberOfText: PropTypes.number,
    TemplateThumbnailUrl: PropTypes.string.isRequired,
    TemplatePreviewUrl: PropTypes.string.isRequired,
    // NumberOfLogos: PropTypes.string.isRequired,
  })).isRequired,
  setClickedTemplate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  getAssetCountInfo: PropTypes.func.isRequired,
  templateTagOptions: PropTypes.shape({
    all: PropTypes.string.isRequired,
    brandAwareness: PropTypes.string.isRequired,
    performance: PropTypes.string.isRequired,
    premiumStreaming: PropTypes.string.isRequired,
    onlineVideo: PropTypes.string.isRequired,
  }).isRequired,
  lastTriggerRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
};

TemplateGrid.defaultProps = {
  loading: true,
};
